import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UserService } from 'src/app/services/user.service';
import { ApplicationService } from '../../services/application.service';
import { CompanyService } from '../../services/company.service';

import { UserInfo, LogonRequest, LogonUserInformation } from '../../models/user';
import { MessageListItem } from '../../models/application';
import { VerifyService } from '../../services/verify.service';
import { LogService } from '../../services/log.service';
import { Timestamp } from 'rxjs/internal/operators/timestamp';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit {
    @ViewChild('userPasswordRef', { static: false }) userPasswordRef: ElementRef;

    private _handleError: HandleError;
    private _logging = environment.logging;

    // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

    showWelcomeMessagesSpinner = false;
    showTemporaryMessagesSpinner = false;
    showGeneralMessagesSpinner = false;             //01/10/2023 - 1098-E Message

    showLoginTimeoutError = false;

    welcomeMessageList: MessageListItem[] = [];
    temporaryMessageList: MessageListItem[] = [];
    generalMessageList: MessageListItem[] = [];     //01/10/2023 - 1098-E Message

    loginForm: UntypedFormGroup;
    userNameFormControl = new UntypedFormControl();
    userPasswordFormControl = new UntypedFormControl();

    logonRequest: LogonRequest;
    logonUserInformation: LogonUserInformation;

    userNameEnabled = true;
    userNameVisible = true;
    userNameButtonVisible = true;
    showVerifyUserNameError = false;
    showLockedOutError = false;
    showTemporaryPasswordError = false;
    showExpiredTemporaryPasswordError = false;

    userPasswordButtonVisible = false;
    userPasswordVisible = false;
    showLoginError = false;
    showBlockedIPError = false;

    registerVisible = true;

    showLoginSpinner = false;

    userInfo: UserInfo;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _errorHandler: ErrorHandler,
        private _userService: UserService,
        private _verifyService: VerifyService,
        private _logService: LogService,
        private _applicationService: ApplicationService,
        private _companyService: CompanyService,
        public media: MediaObserver
    ) {
        this._handleError = _errorHandler.createHandleError('LoginComponent');

        this.logonRequest = this._userService.getNewLogonRequest();
        this.logonUserInformation = this._userService.getNewLogonUserInformation();

        this.getData();
        this.createForm();
        this.scrollToTop();
    }

    ngOnInit() {
        this._userService.userInfo.subscribe(response => this._onUserInfoChanged(response), error => this._handleError(error));
    }

    getData(): void {
        const companyCode: string = this._companyService.getCompanyCode();

        this.showWelcomeMessagesSpinner = true;
        this.getWelcomeMessageList(companyCode);

        this.showTemporaryMessagesSpinner = true;
        this.getTemporaryMessageList(companyCode);

        // 01/10/2023 - 1098-E Message
        this.showGeneralMessagesSpinner = true;
        this.getGeneralMessageList(companyCode);
    }

    getWelcomeMessageList(companyCode: string): void {
        this.showWelcomeMessagesSpinner = true;
        this._applicationService.getWelcomeMessageList(companyCode).subscribe(
            resultArray => {
                this.welcomeMessageList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('LoginComponent.getWelcomeMessageList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('LoginComponent.getWelcomeMessageList :: result = ' + JSON.stringify(resultArray));
                }
                this.showWelcomeMessagesSpinner = false;
            },
            error => this._handleError('getWelcomeMessageList', error)
        );
    }

    getTemporaryMessageList(companyCode: string): void {
        this.showTemporaryMessagesSpinner = true;
        this._applicationService.getTemporaryMessageList(companyCode).subscribe(
            resultArray => {
                this.temporaryMessageList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('LoginComponent.getTemporaryMessageList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('LoginComponent.getTemporaryMessageList :: result = ' + JSON.stringify(resultArray));
                }
                this.showTemporaryMessagesSpinner = false;
            },
            error => this._handleError('getTemporaryMessageList', error)
        );
    }

    //01/10/2023 - 1098-E Message
    getGeneralMessageList(companyCode: string): void {
        this.showGeneralMessagesSpinner = true;
        this._applicationService.getGeneralMessageList(companyCode, "generalBold").subscribe(
            resultArray => {
                this.generalMessageList = resultArray;
                if (this._logging.serviceCallTraceLog) {
                    console.log('LoginComponent.getGeneralMessageList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('LoginComponent.getGeneralMessageList :: result = ' + JSON.stringify(resultArray));
                }
                this.showGeneralMessagesSpinner = false;
            },
            error => this._handleError('getGeneralMessageList', error)
        );
    }

    getMessageCssClass(item: MessageListItem): string {
        return this._applicationService.getMessageListItemDisplayClass(item);
    }

    createForm(): void {
        this.loginForm = this._formBuilder.group({});
        this.loginForm.addControl('userNameFormControl', this.userNameFormControl);
        this.loginForm.addControl('userPasswordFormControl', this.userPasswordFormControl);

        this.loginForm.setValue({
            userNameFormControl: null,
            userPasswordFormControl: null
        });

        this.loginForm.get('userNameFormControl').setValidators([Validators.required]);
        this.loginForm.get('userPasswordFormControl').setValidators([Validators.required]);

        this.loginForm.get('userNameFormControl').updateValueAndValidity();
        this.loginForm.get('userPasswordFormControl').updateValueAndValidity();
    }

    resetForm(): void {
        this.loginForm.setValue({
            userNameFormControl: '',
            userPasswordFormControl: ''
        });

        this.loginForm.controls['userNameFormControl'].markAsPristine();
        this.loginForm.controls['userNameFormControl'].markAsUntouched();
        this.loginForm.controls['userPasswordFormControl'].markAsPristine();
        this.loginForm.controls['userPasswordFormControl'].markAsUntouched();
        this.loginForm.get('userNameFormControl').updateValueAndValidity();
        this.loginForm.get('userPasswordFormControl').updateValueAndValidity();

        this.showLoginSpinner = false;

        this.userNameEnabled = true;
        this.userNameVisible = true;
        this.userNameButtonVisible = true;
        this.showVerifyUserNameError = false;
        this.showLockedOutError = false;
        this.showTemporaryPasswordError = false;
        this.showExpiredTemporaryPasswordError = false;

        this.userPasswordButtonVisible = false;
        this.userPasswordVisible = false;
        this.showLoginError = false;
        this.showBlockedIPError = false;

        this.registerVisible = true;
    }

    loginUserEnabled(): boolean {
        return this.userNameButtonVisible && this.loginForm.get('userNameFormControl').valid;
    }

    verifyUserName(): void {
        if (this.loginForm.get('userNameFormControl').valid) {
            this.showLoginSpinner = true;
            this.userNameButtonVisible = false;
            this.showVerifyUserNameError = false;
            this.showLockedOutError = false;
            this.showTemporaryPasswordError = false;
            this.showExpiredTemporaryPasswordError = false;

            this.logonRequest = this._userService.getNewLogonRequest();
            this.logonRequest.userName = this.loginForm.get('userNameFormControl').value;

            this._userService.verifyUserName(this.logonRequest).subscribe(
                result => {
                    if (this._logging.serviceCallTraceLog) {
                        console.log('LoginComponent.verifyUserName :: result.userId = ' + result.userId);
                    }
                    if (this._logging.serviceDataTraceLog) {
                        console.log('LoginComponent.verifyUserName :: result = ' + JSON.stringify(result));
                    }

                    this.showLoginSpinner = false;

                    this.logonUserInformation = result;

                    if (this.logonUserInformation !== null && this.logonUserInformation.userId !== null && this.logonUserInformation.userId > 0) {
                        if (this.logonUserInformation.activeFlag) {
                            // UserName has been verified and the account is not locked out
                            this.userNameButtonVisible = false;
                            this.userNameEnabled = false;
                            this.showVerifyUserNameError = false;
                            this.showLockedOutError = false;
                            this.showTemporaryPasswordError = false;
                            this.showExpiredTemporaryPasswordError = false;

                            this.userPasswordVisible = true;
                            this.userPasswordButtonVisible = true;
                            this.showLoginError = false;
                            this.showBlockedIPError = false;

                            this.registerVisible = false;

                            setTimeout(() => {
                                this.userPasswordRef.nativeElement.focus();
                            });
                        } else {
                            if (this.logonUserInformation.lockedOutFlag) {
                                // UserName has been verified but the account is locked out
                                this.userNameButtonVisible = true;
                                this.userNameEnabled = true;
                                this.showVerifyUserNameError = false;
                                this.showLockedOutError = true;
                                this.showTemporaryPasswordError = false;
                                this.showExpiredTemporaryPasswordError = false;

                                this.userPasswordVisible = false;
                                this.userPasswordButtonVisible = false;
                                this.showLoginError = false;
                                this.showBlockedIPError = false;

                                this.registerVisible = false;
                            } else if (this.logonUserInformation.temporaryPasswordFlag) {
                                // UserName has been verified but a temporary password exists
                                this.userNameButtonVisible = true;
                                this.userNameEnabled = true;
                                this.showVerifyUserNameError = false;
                                this.showLockedOutError = false;
                                this.showTemporaryPasswordError = true;
                                this.showExpiredTemporaryPasswordError = false;

                                this.userPasswordVisible = false;
                                this.userPasswordButtonVisible = false;
                                this.showLoginError = false;
                                this.showBlockedIPError = false;

                                this.registerVisible = false;
                            } else if (this.logonUserInformation.temporaryPasswordExpiredFlag) {
                                // UserName has been verified but the temporary password has expired
                                this.userNameButtonVisible = true;
                                this.userNameEnabled = true;
                                this.showVerifyUserNameError = false;
                                this.showLockedOutError = false;
                                this.showTemporaryPasswordError = false;
                                this.showExpiredTemporaryPasswordError = true;

                                this.userPasswordVisible = false;
                                this.userPasswordButtonVisible = false;
                                this.showLoginError = false;
                                this.showBlockedIPError = false;

                                this.registerVisible = false;
                            } else {
                                // Something else is wrong and the user is not active
                                this.userNameButtonVisible = true;
                                this.userNameEnabled = true;
                                this.showVerifyUserNameError = true;
                                this.showLockedOutError = false;
                                this.showTemporaryPasswordError = false;
                                this.showExpiredTemporaryPasswordError = false;

                                this.userPasswordVisible = false;
                                this.userPasswordButtonVisible = false;
                                this.showLoginError = false;
                                this.showBlockedIPError = false;

                            }
                        }
                    } else {
                        // UserName could not be verified
                        this.userNameButtonVisible = true;
                        this.userNameEnabled = true;
                        this.showVerifyUserNameError = true;
                        this.showLockedOutError = false;
                        this.showTemporaryPasswordError = false;
                        this.showExpiredTemporaryPasswordError = false;

                        this.userPasswordVisible = false;
                        this.userPasswordButtonVisible = false;
                        this.showLoginError = false;
                        this.showBlockedIPError = false;

                        this.registerVisible = true;
                    }
                },
                error => {
                    this._handleError('verifyUserName', error, this.logonRequest);
                    this.showLoginSpinner = false;
                    this.userNameEnabled = true;
                    this.userNameButtonVisible = true;
                    this.showVerifyUserNameError = true;
                    this.showLockedOutError = false;
                    this.showTemporaryPasswordError = false;
                    this.showExpiredTemporaryPasswordError = false;

                    this.userPasswordVisible = false;
                    this.userPasswordButtonVisible = false;
                    this.showLoginError = false;
                    this.showBlockedIPError = false;

                    this.registerVisible = true;
                }
            );
        } else {
            this.showLoginSpinner = false;
            this.userNameEnabled = true;
            this.userNameButtonVisible = true;
            this.showVerifyUserNameError = false;
            this.showLockedOutError = false;
            this.showTemporaryPasswordError = false;
            this.showExpiredTemporaryPasswordError = false;

            this.userPasswordVisible = false;
            this.userPasswordButtonVisible = false;
            this.showLoginError = false;
            this.showBlockedIPError = false;

            this.registerVisible = true;
        }
    }

    submitUser(): void {

        let ipAddress: string = '';

        if (this.loginForm.get('userNameFormControl').valid) {
            this.showLoginSpinner = true;
            this.userPasswordButtonVisible = false;
            this.showLoginError = false;
            this.showBlockedIPError = false;

            this.logonRequest.password = this.loginForm.get('userPasswordFormControl').value;

            // get public IP address first (key for verification)
            this._userService.getPublicIpAddress().subscribe(
                result => {
                    ipAddress = result.ip;

                    if (this._logging.serviceCallTraceLog) {
                        this._logService.log('LoginComponent', 'submitUser', 'VerifyService.getPublicIpAddress :: result.ip = ' + result.ip, JSON.stringify(result));
                        console.log('VerifyService.getPublicIpAddress :: result.ip = ' + result.ip);
                    }
                    if (this._logging.serviceDataTraceLog) {
                        this._logService.log('LoginComponent', 'submitUser', 'VerifyService.getPublicIpAddress :: result = ' + JSON.stringify(result), JSON.stringify(result));
                        console.log('VerifyService.getPublicIpAddress :: result = ' + JSON.stringify(result));
                    }

                    if (ipAddress === null || ipAddress === '') {

                        this._handleError('getPublicIpAddress', new Error('Public IP Address could not be retrieved'));
                        this.showLoginSpinner = false;
                        this.userPasswordVisible = true;
                        this.userPasswordButtonVisible = true;
                        this.showLockedOutError = false;
                        this.showTemporaryPasswordError = false;
                        this.showExpiredTemporaryPasswordError = false;
                        this.showLoginError = false;
                        this.showBlockedIPError = true;

                        return;
                    }

                    // login in next
                    this.logonRequest.ipAddress = ipAddress;
                    this._userService.verifyUserNamePassword(this.logonRequest).subscribe(
                        result => {
                            if (this._logging.serviceCallTraceLog) {
                                this._logService.log('LoginComponent', 'submitUser', 'LoginComponent.submitUser.verifyUserNamePassword :: result.participantId = ' + result.participantId, JSON.stringify(result));
                            }
                            if (this._logging.serviceDataTraceLog) {
                                this._logService.log('LoginComponent', 'submitUser', 'LoginComponent.submitUser.verifyUserNamePassword :: result = ' + JSON.stringify(result), JSON.stringify(result));
                            }

                            this.showLoginSpinner = false;

                            this.logonUserInformation = result;

                            // Set Verify Information
                            this._verifyService.setVerify(
                                this.logonUserInformation.participantId,
                                this.logonRequest.userName,
                                this._companyService.getCompanyCode(),
                                this.logonUserInformation.multifactorEligible,
                                this.logonUserInformation.multifactorEnabled,
                                this.logonUserInformation.multifactorGracePeriod,
                                this.logonUserInformation.lastVerifiedIpAddress,
                                this.logonUserInformation.lastVerifiedOTP,
                                this.logonUserInformation.multifactorRetryCount,
                                "",
                                false,
                                this.logonUserInformation.mfaChallengeToken
                            )

                            this._verifyService.setCurrentIPAddress(ipAddress);

                            if (
                                this.logonUserInformation !== null
                                && this.logonUserInformation.userId !== null
                                && this.logonUserInformation.userId > 0
                                && this.logonUserInformation.participantId !== null
                                && this.logonUserInformation.participantId > 0
                                && this.logonUserInformation.securityQuestionSetupFlag !== null
                            ) {
                                // Set Login Information
                                this._userService.login(
                                    this.logonUserInformation.userId,
                                    this.logonRequest.userName,
                                    this.logonUserInformation.participantId,
                                    this.logonUserInformation.firstName,
                                    this.logonUserInformation.middleInitial,
                                    this.logonUserInformation.lastName,
                                    this.logonUserInformation.securityQuestionSetupFlag,
                                    this.logonUserInformation.accessToken,
                                    this.logonUserInformation.accessTokenExpiresAtUtc
                                );

                                this._userService.setLogonUserInformation(this.logonUserInformation);

                                if (this.logonUserInformation.mfaRequired) {
                                    this._router.navigate(['verify']);
                                } else if (this.logonUserInformation.securityQuestionSetupFlag) {
                                    this._openHome();
                                } else {
                                    this._openUserSecurityQuestion();
                                }

                            } else {
                                // Check to see if the user has been locked out
                                this.logonRequest.password = null;
                                this._userService.verifyUserName(this.logonRequest).subscribe(
                                    result2 => {
                                        if (this._logging.serviceCallTraceLog) {
                                            this._logService.log('LoginComponent', 'submitUser', 'LoginComponent.submitUser.verifyUserName :: result2.userId = ' + result2.userId, JSON.stringify(result2));
                                        }
                                        if (this._logging.serviceDataTraceLog) {
                                            this._logService.log('LoginComponent', 'submitUser', 'LoginComponent.submitUser.verifyUserName :: result2 = ' + JSON.stringify(result2), JSON.stringify(result2));
                                        }

                                        this.showLoginSpinner = false;

                                        this.logonUserInformation = result2;

                                        if (
                                            this.logonUserInformation !== null &&
                                            this.logonUserInformation.userId !== null &&
                                            this.logonUserInformation.userId > 0
                                        ) {
                                            if (this.logonUserInformation.lockedOutFlag) {
                                                // UserName has been verified but the account is locked out
                                                this.userPasswordVisible = true;
                                                this.userPasswordButtonVisible = true;
                                                this.showLockedOutError = true;
                                                this.showTemporaryPasswordError = false;
                                                this.showExpiredTemporaryPasswordError = false;
                                                this.showLoginError = false;
                                                this.showBlockedIPError = false;

                                            }
                                            else {
                                                // UserName has been verified and the account is not locked out
                                                this.userPasswordVisible = true;
                                                this.userPasswordButtonVisible = true;
                                                this.showLockedOutError = false;
                                                this.showTemporaryPasswordError = false;
                                                this.showExpiredTemporaryPasswordError = false;
                                                this.showLoginError = true;
                                                this.showBlockedIPError = false;

                                            }
                                        } else {
                                            // UserName could not be verified
                                            this.userPasswordVisible = true;
                                            this.userPasswordButtonVisible = true;
                                            this.showLockedOutError = false;
                                            this.showTemporaryPasswordError = false;
                                            this.showExpiredTemporaryPasswordError = false;
                                            this.showLoginError = true;
                                            this.showBlockedIPError = false;

                                        }
                                    },
                                    error => {
                                        this._handleError('verifyUserName', error, this.logonRequest);
                                        this.showLoginSpinner = false;
                                        this.userPasswordVisible = true;
                                        this.userPasswordButtonVisible = true;
                                        this.showLockedOutError = false;
                                        this.showTemporaryPasswordError = false;
                                        this.showExpiredTemporaryPasswordError = false;
                                        this.showLoginError = true;
                                        this.showBlockedIPError = false;

                                    }
                                );
                            }
                        },
                        error => {
                            this._handleError('verifyUserNamePassword', error, this.logonRequest);
                            this.showLoginSpinner = false;
                            this.userPasswordVisible = true;
                            this.userPasswordButtonVisible = true;
                            this.showLockedOutError = false;
                            this.showTemporaryPasswordError = false;
                            this.showExpiredTemporaryPasswordError = false;
                            this.showLoginError = true;
                            this.showBlockedIPError = false;

                        }
                    );
                },
                error => this._handleError('getPublicIpAddress', error)
            );

        } else {
            this.showLoginSpinner = false;
            this.userPasswordVisible = true;
            this.userPasswordButtonVisible = true;
            this.showLockedOutError = false;
            this.showTemporaryPasswordError = false;
            this.showExpiredTemporaryPasswordError = false;
            this.showLoginError = true;
            this.showBlockedIPError = false;
        }
    }

    submitUserEnabled(): boolean {
        return this.userPasswordVisible && this.loginForm.get('userPasswordFormControl').valid;
    }

    registerUser(): void {
        if (this._logging.navigationTraceLog) {
            console.log('LoginComponent.registerUser');
        }
        this._router.navigate(['register']);
    }

    cancelLogin(): void {
        this.resetForm();
    }

    forgotUserName(): void {
        if (this._logging.navigationTraceLog) {
            console.log('LoginComponent.forgotUserName');
        }
        this._router.navigate(['forgotusername']);
    }

    forgotPassword(): void {
        if (this._logging.navigationTraceLog) {
            console.log('LoginComponent.forgotPassword');
        }
        this._router.navigate(['forgotpassword']);
    }

    scrollToTop() {
        const contentStart = document.getElementsByClassName('mat-sidenav-content');
        if (contentStart.length > 0) {
            contentStart[0].scrollTop = 0;
        }
    }

    private _onUserInfoChanged(newUserInfo: UserInfo): void {
        this.showLoginTimeoutError = newUserInfo.sessionTimeout;
        if (this.showLoginSpinner && newUserInfo.authenticated) {
            this.showLoginSpinner = false;
            this._openHome();
        }
    }

    private _openHome(): void {
        if (this._logging.navigationTraceLog) {
            console.log('LoginComponent._openHome');
        }

        //01/19/2026 RA - Check if user needs to be routed to Profile page to update invalid user information.`
        let todayMin = 0;
        const today: Date = new Date();                                     // Get today's date.
        const targetDateString = this.logonUserInformation.lastAccessed;    // The last login date for the user as string
        const targetDate_Dt = new Date(targetDateString);                   // Convert last login date String to Date
        const createdDateString = this.logonUserInformation.createdDate;    // The creation date for the user as string
        const createdDate_Dt = new Date(createdDateString);                 // Convert creation date String to Date
        if (targetDate_Dt > createdDate_Dt) {
            todayMin = 36001;                                               // Set to over an hour if last login date is after creation date
        }
        else {
            todayMin = today.getMinutes() - createdDate_Dt.getMinutes();    // Get the difference in minutes between creation date and today's date
        }


        const numDays = today.getTime() - targetDate_Dt.getTime();          // Get time in milliseconds
        const diffDays2 = Math.floor(numDays / (1000 * 60 * 60 * 24));      // Convert milliseconds to days and round down to nearest day.

        this._userService.setLogonUserInformation(this.logonUserInformation);  //01/19/2026 RA Send data to User Service

        if ((diffDays2 < this.logonUserInformation.daysLastLogin) && (this.logonUserInformation.createdDateMin < todayMin) &&
            (this.logonUserInformation.invalidAddr === 'N') &&
            (this.logonUserInformation.invalidPhone === 'N') && (this.logonUserInformation.invalidEmail === 'N')) {
          this._router.navigate(['']);
        }
        else {
          this._router.navigate(['profile']);           //12/22/2025 RA - Route user to the Profile page to check for valid user information.
        }
    }

    private _openUserSecurityQuestion(): void {
        if (this._logging.navigationTraceLog) {
            console.log('LoginComponent._openUserSecurityQuestion');
        }
        this._router.navigate(['securityquestion']);
    }
}
